import { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import ToastMsg from "../../components/common/ToastMsg";
import { SETTINGS_CHANGE_PASSWORD } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import { Eye, CheckCircle2, XCircle, AlertTriangle, LogOut, Key, Shield, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { cn } from "../../utils/tailwindUtils";
import { PasswordField } from "../../components/ui/form-fields";

/* ----------------------------- theme primitives ---------------------------- */

const SettingCard = ({ icon: Icon, title, subtitle, action, children, className }) => (
    <div className={cn("bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5", className)}>
        <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
            <span className="w-9 h-9 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                <Icon className="h-4.5 w-4.5" />
            </span>
            <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
                {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
            </div>
            {action}
        </div>
        {children}
    </div>
);

const PasswordInput = ({ value, onChange, placeholder, label, autoComplete = "new-password", name }) => (
    <PasswordField
        name={name}
        autoComplete={autoComplete}
        label={label}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        id={name || "security-password"}
    />
);

const PasswordRequirement = ({ met, text }) => (
    <div className="flex items-center gap-2.5">
        {met ? (
            <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
        ) : (
            <XCircle className="h-4 w-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
        )}
        <span className={cn("text-sm", met ? "text-gray-700 dark:text-gray-300" : "text-gray-400 dark:text-gray-500")}>
            {text}
        </span>
    </div>
);

/* --------------------------------- screen ---------------------------------- */

const Security = () => {
    let navigate = useNavigate();
    const { logoutauth } = useContext(AuthContext);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [fieldErrors, setFieldErrors] = useState({ current: "", next: "", confirm: "" });
    const [loading, setLoading] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [logoutReason, setLogoutReason] = useState("manual"); // manual | password_changed

    useEffect(() => {
        if (!showLogoutConfirm) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") setShowLogoutConfirm(false);
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [showLogoutConfirm]);

    const { create } = useApiMethods();

    const passwordPolicy = sessionStorage.getItem("password_policy");
    const parsedPolicy = passwordPolicy ? JSON.parse(passwordPolicy) : null;

    const validatePassword = (password) => {
        if (!parsedPolicy) return { isValid: true, requirements: [] };
        const p = parsedPolicy;
        // Mirror the backend (tools/validate.validate_password_policies) exactly:
        // enforce BOTH minimum and maximum for each class, and treat "special
        // chars" as anything that isn't a letter, digit or whitespace.
        const count = (re) => (password.match(re) || []).length;
        const digits = count(/[0-9]/g);
        const lower = count(/[a-z]/g);
        const upper = count(/[A-Z]/g);
        const special = count(/[^A-Za-z0-9\s]/g);
        const inRange = (n, min, max) =>
            n >= (min ?? 0) && n <= (max ?? Number.MAX_SAFE_INTEGER);

        const requirements = [
            { met: inRange(password.length, p.min_length, p.max_length), text: `Between ${p.min_length ?? 1} and ${p.max_length ?? 15} characters` },
            { met: inRange(digits, p.min_number, p.max_number), text: `${p.min_number ?? 0} to ${p.max_number ?? 0} number(s)` },
            { met: inRange(lower, p.min_lower, p.max_lower), text: `${p.min_lower ?? 0} to ${p.max_lower ?? 0} lowercase letter(s)` },
            { met: inRange(upper, p.min_upper, p.max_upper), text: `${p.min_upper ?? 0} to ${p.max_upper ?? 0} uppercase letter(s)` },
            p.password_combination
                ? { met: inRange(special, p.min_chars, p.max_chars), text: `${p.min_chars ?? 0} to ${p.max_chars ?? 0} special character(s)` }
                : { met: special === 0, text: `No special characters allowed` },
        ];
        return { isValid: requirements.every((req) => req.met), requirements };
    };

    const { isValid: isNewPasswordValid, requirements: newPasswordRequirements } = validatePassword(newPassword);

    const handleChangePassword = async () => {
        const nextErrors = { current: "", next: "", confirm: "" };
        if (!currentPassword) nextErrors.current = "Current password is required";
        if (!newPassword) nextErrors.next = "New password is required";
        if (!confirmPassword) nextErrors.confirm = "Please confirm the new password";
        if (currentPassword && newPassword && currentPassword === newPassword) {
            nextErrors.next = "New password cannot be the same as current password";
        }
        if (newPassword && confirmPassword && newPassword !== confirmPassword) {
            nextErrors.confirm = "New password and confirm password do not match";
        }
        if (parsedPolicy && newPassword && !isNewPasswordValid) {
            nextErrors.next = "New password does not meet the requirements";
        }
        setFieldErrors(nextErrors);
        if (nextErrors.current || nextErrors.next || nextErrors.confirm) {
            ToastMsg("error", nextErrors.current || nextErrors.next || nextErrors.confirm);
            return;
        }

        setLoading(true);
        try {
            const payload = { current_password: currentPassword, new_password: newPassword };
            const response = await create(SETTINGS_CHANGE_PASSWORD, payload);
            if (response.status === "success") {
                ToastMsg("success", response.message);
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
                setFieldErrors({ current: "", next: "", confirm: "" });
                setLogoutReason("password_changed");
                setShowLogoutConfirm(true);
            } else {
                ToastMsg("error", response?.message || "Failed to update password");
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleLogoutAllDevices = () => {
        ToastMsg("success", "Logged out from all devices");
        setShowLogoutConfirm(false);
        logoutauth();
        navigate("/");
    };

    const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                    <Shield className="h-5 w-5" />
                </div>
                <div>
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Security & Access</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Manage your password and account security settings.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                {/* Change Password */}
                <SettingCard icon={Key} title="Change Password" subtitle="Update your password to keep your account secure.">
                    <form
                        autoComplete="off"
                        onSubmit={(e) => e.preventDefault()}
                        className="space-y-4"
                    >
                        <input
                            type="text"
                            name="username"
                            autoComplete="username"
                            tabIndex={-1}
                            aria-hidden="true"
                            className="sr-only"
                            value=""
                            readOnly
                        />
                        <PasswordInput
                            value={currentPassword}
                            onChange={(e) => {
                                setCurrentPassword(e.target.value);
                                setFieldErrors((prev) => ({ ...prev, current: "" }));
                            }}
                            placeholder="Enter your current password"
                            label="Current Password"
                            name="settings-current-password"
                            autoComplete="current-password"
                        />
                        {fieldErrors.current && (
                            <p className="text-sm text-red-600 dark:text-red-400 -mt-2">{fieldErrors.current}</p>
                        )}
                        <PasswordInput
                            value={newPassword}
                            onChange={(e) => {
                                setNewPassword(e.target.value);
                                setFieldErrors((prev) => ({ ...prev, next: "" }));
                            }}
                            placeholder="Create a new password"
                            label="New Password"
                            name="settings-new-password"
                            autoComplete="new-password"
                        />
                        {fieldErrors.next && (
                            <p className="text-sm text-red-600 dark:text-red-400 -mt-2">{fieldErrors.next}</p>
                        )}

                        {parsedPolicy && (
                            <div className="rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 p-3.5">
                                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2.5 flex items-center gap-2">
                                    <Shield className="h-4 w-4 text-sidebar-primary" />
                                    Password Requirements
                                </h3>
                                <div className="space-y-2">
                                    {newPasswordRequirements.map((requirement, index) => (
                                        <PasswordRequirement key={index} met={requirement.met} text={requirement.text} />
                                    ))}
                                </div>
                            </div>
                        )}

                        <PasswordInput
                            value={confirmPassword}
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                setFieldErrors((prev) => ({ ...prev, confirm: "" }));
                            }}
                            placeholder="Confirm your new password"
                            label="Confirm New Password"
                            name="settings-confirm-password"
                            autoComplete="new-password"
                        />
                        {fieldErrors.confirm && (
                            <p className="text-sm text-red-600 dark:text-red-400 -mt-2">{fieldErrors.confirm}</p>
                        )}

                        {newPassword && confirmPassword && (
                            <div className={cn(
                                "flex items-center gap-2.5 p-2.5 rounded-lg border text-sm font-medium",
                                passwordsMatch
                                    ? "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800 text-green-700 dark:text-green-300"
                                    : "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800 text-red-700 dark:text-red-300"
                            )}>
                                {passwordsMatch ? <CheckCircle2 className="h-4.5 w-4.5 flex-shrink-0" /> : <XCircle className="h-4.5 w-4.5 flex-shrink-0" />}
                                <span>{passwordsMatch ? "Passwords match" : "Passwords do not match"}</span>
                            </div>
                        )}

                        <Button
                            type="button"
                            variant="brand"
                            onClick={handleChangePassword}
                            disabled={loading}
                            className="w-full font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            {loading ? (
                                <>
                                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Changing Password...
                                </>
                            ) : (
                                <>
                                    <Key className="h-4 w-4" />
                                    Update Password
                                </>
                            )}
                        </Button>
                    </form>
                </SettingCard>

                {/* Right column */}
                <div className="space-y-6">
                    {/* Security Status */}
                    <SettingCard
                        icon={Shield}
                        title="Security Status"
                        subtitle="Overview of your account protection."
                        action={
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Protected
                            </span>
                        }
                    >
                        <div className="space-y-3">
                            {/* Password expiry */}
                            <div className="flex items-center justify-between rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 px-3.5 py-3">
                                <div className="flex items-center gap-3">
                                    <span className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 grid place-items-center">
                                        <Clock className="h-4.5 w-4.5" />
                                    </span>
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Password Expiry</h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Days until reset required</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xl font-bold text-amber-600 dark:text-amber-400">
                                        {parsedPolicy?.expairy_period || 0}
                                    </div>
                                    <div className="text-xs text-gray-400">days left</div>
                                </div>
                            </div>

                            {/* Session security */}
                            <div className="flex items-start gap-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 px-3.5 py-3">
                                <span className="w-9 h-9 rounded-lg bg-sidebar/10 text-sidebar-primary grid place-items-center flex-shrink-0">
                                    <Shield className="h-4.5 w-4.5" />
                                </span>
                                <div className="min-w-0">
                                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Session Security</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                                        {parsedPolicy?.session_time_out ? (
                                            <span className="flex items-center gap-2">
                                                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                                Auto-logout after{" "}
                                                <span className="font-semibold text-sidebar-primary">
                                                    {parsedPolicy.time_out_value || 30} minutes
                                                </span>{" "}
                                                of inactivity
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-2">
                                                <span className="w-2 h-2 bg-gray-400 rounded-full" />
                                                Session timeout disabled
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </SettingCard>

                    {/* Danger Zone */}
                    <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50/60 dark:bg-red-900/10 shadow-sm p-5">
                        <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-red-200/60 dark:border-red-800/60">
                            <span className="w-9 h-9 flex-shrink-0 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 grid place-items-center">
                                <AlertTriangle className="h-4.5 w-4.5" />
                            </span>
                            <div>
                                <h2 className="text-base font-semibold text-red-800 dark:text-red-300">Danger Zone</h2>
                                <p className="text-xs text-red-700/80 dark:text-red-400/80 mt-0.5">Critical actions affecting your account.</p>
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 p-3.5 bg-white dark:bg-gray-800 rounded-xl border border-red-200 dark:border-red-800">
                            <div className="min-w-0">
                                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Logout All Devices</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Sign out from all active sessions.</p>
                            </div>
                            <Button
                                onClick={() => {
                                    setLogoutReason("manual");
                                    setShowLogoutConfirm(true);
                                }}
                                className="bg-red-600 hover:bg-red-700 text-white flex-shrink-0 flex items-center gap-1.5"
                            >
                                <LogOut className="h-4 w-4" />
                                Logout All
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {showLogoutConfirm && createPortal(
                <ConfirmPopupModal
                    open={showLogoutConfirm}
                    onClose={() => setShowLogoutConfirm(false)}
                    onConfirm={handleLogoutAllDevices}
                    title={logoutReason === "password_changed" ? "Password Updated Successfully" : "Logout All Devices?"}
                    description={
                        logoutReason === "password_changed"
                            ? "Your password has been changed. For security, please sign in again on all devices."
                            : "This will immediately sign you out from all active sessions across all devices. You'll need to log in again on each device."
                    }
                    type="danger"
                    destructive
                    confirmLabel={logoutReason === "password_changed" ? "Logout & Sign In Again" : "Confirm Logout"}
                    cancelLabel="Cancel"
                />,
                document.body
            )}
        </div>
    );
};

export default Security;
